import clsx from 'clsx';
import React from 'react';
import { AiOutlineMinusCircle, AiOutlinePlusCircle } from 'react-icons/ai';
import { toast } from 'react-toastify';

import { useAgentEdit } from '@/app/agents/edit/state/agentEditContext';
import { validateVariableName } from '@/app/agents/edit/util/validation';
import { Text } from '@/components/typography/text';
import {
  AgentConfigStepInputs,
  AgentConfigStepLLMActionTool,
  AgentConfigStepRoute,
  AgentConfigStepRouteOutputs,
  AgentConfigStepRouteOutputTypeEnum,
  AgentConfigStepRoutes
} from '@/types/agent';

import { getNextNewKey } from '../../util/getNextNewKey';
import styles from './configEditPanel.module.scss';


const ENTITY = 'output';

type Props = {
  editable: boolean;
  inputs: AgentConfigStepInputs;
  onRoutesChange: ( routes: AgentConfigStepRoutes ) => void;
  routes: AgentConfigStepRoutes;
  stepId: string;
  tools?: AgentConfigStepLLMActionTool[];
  steps: string[];
};

export default function PaneRoute( props: Props ) {
  const { editable, inputs, onRoutesChange, routes, stepId, tools, steps } = props;
  const { validationErrors } = useAgentEdit();

  const listItems = steps.map( ( stepId: string ) => (
    <option key={ stepId } value={ stepId }>
      { stepId }
    </option>
  ) );

  const updateOutputVarKey = (
    event: React.FormEvent<HTMLInputElement>,
    routeKey: string,
    oldOutputKey: string
  ) => {
    const newOutputKey = event.currentTarget.value;

    // Update the output value
    const nextOutputs: AgentConfigStepRouteOutputs = {};
    for ( const k of Object.keys( routes[ routeKey ].outputs ) ) {
      nextOutputs[ k === oldOutputKey ? newOutputKey : k ] = routes[ routeKey ].outputs[ k ];
    }

    const nextRoutes = {
      ...routes,
      [ routeKey ]: { ...routes[ routeKey ], outputs: nextOutputs }
    };
    onRoutesChange( nextRoutes );

    // Show toast for immediate feedback if invalid
    const error = validateVariableName( newOutputKey );
    if ( error ) {
      toast.error( error.message );
    }
  };

  const updateKey = (
    event: React.ChangeEvent<HTMLSelectElement>,
    routeKey: string,
    outputKey: string
  ) => {
    const outputKeyVal = event.currentTarget.value;

    let isFromTool = false;

    if ( typeof tools != 'undefined' ) {
      for ( const tool of tools ) {
        if ( tool.function.name === routeKey ) {
          if ( Object.keys( tool.function.parameters.properties ).includes( outputKeyVal ) ) {
            isFromTool = true;
            break;
          }
        }
      }
    }

    const type =
      outputKeyVal === 'response' ||
      outputKeyVal === 'route' ||
      outputKeyVal === 'merged_conversation' ||
      isFromTool
        ? AgentConfigStepRouteOutputTypeEnum.FROM_ACTION
        : AgentConfigStepRouteOutputTypeEnum.FROM_INPUT;

    const nextRoutes = {
      ...routes,
      [ routeKey ]: {
        ...routes[ routeKey ],
        outputs: {
          ...routes[ routeKey ].outputs,
          [ outputKey ]: { key: outputKeyVal, type }
        }
      }
    };

    onRoutesChange( nextRoutes );
  };

  const onAddOutput = ( routeKey: string ) => {
    const newOutputKey = getNextNewKey( Object.keys( routes[ routeKey ].outputs ), ENTITY );

    const nextRoutes = {
      ...routes,
      [ routeKey ]: {
        ...routes[ routeKey ],
        outputs: {
          ...routes[ routeKey ].outputs,
          [ newOutputKey ]: { type: AgentConfigStepRouteOutputTypeEnum.FROM_ACTION, key: 'response' }
        }
      }
    };

    onRoutesChange( nextRoutes );
  };

  const onDelete = ( routeKey: string, outputKey: string ) => {
    const { [ outputKey ]: _, ...nextOutputs } = routes[ routeKey ].outputs;

    const nextRoutes = {
      ...routes,
      [ routeKey ]: { ...routes[ routeKey ], outputs: nextOutputs }
    };

    onRoutesChange( nextRoutes );
  };

  const onStepChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
    routeKey: string
  ) => {
    const nextRoutes = {
      ...routes,
      [ routeKey ]: { ...routes[ routeKey ], step: event.currentTarget.value }
    };

    onRoutesChange( nextRoutes );
  };

  const getToolsVariables = ( tool: string ): string[] => {
    if ( typeof tools === 'undefined' ) return [];

    for ( const t of tools ) {
      if ( t.function.name === tool ) {
        return Object.keys( t.function.parameters.properties );
      }
    }

    return [];
  };

  const inputVariables = inputs && Object.keys( inputs ).map(
    ( keyValue, index ) => (
      <option key={ index } value={ keyValue }>
        {keyValue} (From Input)
      </option>
    )
  );

  return (
    <div className="configPane">
      <Text as="div" className='paneTitle' size="md" weight="bold">Routes</Text>
      {
        Object.entries( routes ?? {} ).map(
          ( [ routeKey, route ]: [ string, AgentConfigStepRoute ], routeIndex ) => (
            <div className="subitem" key={ routeKey }>
              <div>
                <h3>{ routeKey === 'default' ? 'Default Path' : routeKey }</h3>
                <Text as="label" size="sm" htmlFor="goToStep">Go To Step</Text>
                <select
                  data-testid={ `config-edit-panel-route-${ routeIndex }-go-to-select` }
                  disabled={ !editable }
                  id="goToStep"
                  onChange={ ( event ) => onStepChange( event, routeKey ) }
                  value={ route.step }
                >
                  { listItems }
                </select>
                <Text as="label" size="sm" htmlFor="outputs">
                  Outputs
                </Text>
              </div>
              {
                Object.entries( route.outputs ).map(
                  ( [ outputKey, output ], outputIndex ) => {
                    const hasError = !!validationErrors[stepId]?.outputs?.[routeKey]?.[outputKey];
                    const errorMessage = validationErrors[stepId]?.outputs?.[routeKey]?.[outputKey];

                    return (
                      <div className="outputRow" key={ outputIndex }>
                        {
                          editable && (
                            <AiOutlineMinusCircle
                              className="outputIcon"
                              data-testid={ `config-edit-panel-route-${ routeIndex }-output-${ outputKey }-delete-button` }
                              onClick={ () => onDelete( routeKey, outputKey ) }
                            />
                          )
                        }
                        <div
                          className={ clsx( 'outputName', { error: hasError } ) }
                          data-error-message={ hasError ? errorMessage : undefined }
                        >
                          <input
                            className={ clsx( { required: hasError } ) }
                            data-testid={ `config-edit-panel-route-${ routeIndex }-output-${ outputKey }-name-input` }
                            type="text"
                            value={ outputKey }
                            onInput={ ( event ) => updateOutputVarKey( event, routeKey, outputKey ) }
                            disabled={ !editable }
                          />
                        </div>
                        <div className="outputValue">
                          <select
                            data-testid={ `config-edit-panel-route-${ routeIndex }-output-${ outputKey }-value-select` }
                            value={ output.key }
                            onChange={ ( event ) => updateKey( event, routeKey, outputKey ) }
                            disabled={ !editable }
                          >
                            <option value="response">Action Response</option>
                            <option value="route">Action Route</option>
                            <option value="merged_conversation">
                              Action Merged Conversations
                            </option>
                            { inputVariables }
                            {
                              getToolsVariables( routeKey ).map(
                                ( keyValue, index ) => (
                                  <option key={ index } value={ keyValue }>
                                    { keyValue }(From Tool)
                                  </option>
                                )
                              )
                            }
                          </select>
                        </div>
                      </div>
                    );
                  }
                )
              }
              {
                editable && (
                  <div
                    className="addOutputRow"
                    onClick={ () => onAddOutput( routeKey ) }
                    data-testid={ `config-edit-panel-route-${ routeIndex }-add-output-button` }
                  >
                    <AiOutlinePlusCircle className={ styles.outputIcon } />
                    <Text as="span" size="sm">Add output</Text>
                  </div>
                )
              }
            </div>
          )
        )
      }
    </div>
  );
}
